import type { ReactElement } from "react";
import { Navigate, useParams } from "react-router-dom";

import { isUuid } from "@/lib/parseUuidListParam";

export function WorkerEditRedirect(): ReactElement {
  const { id } = useParams<{ id: string }>();
  if (!id || !isUuid(id)) {
    return <Navigate to="/settings/workers" replace />;
  }
  return <Navigate to={`/settings/workers?edit=${id}`} replace />;
}

export const LegacyWorkerEditRedirect = WorkerEditRedirect;

export function LegacyOccurrenceEditRedirect(): ReactElement {
  const { id } = useParams<{ id: string }>();
  if (!id || !isUuid(id)) {
    return <Navigate to="/settings/occurrences" replace />;
  }
  return <Navigate to={`/settings/occurrences?edit=${id}`} replace />;
}

export function WorkerRoleEditRedirect(): ReactElement {
  const { id } = useParams<{ id: string }>();
  if (!id || !isUuid(id)) {
    return <Navigate to="/settings/workers/roles" replace />;
  }
  return <Navigate to={`/settings/workers/roles?edit=${id}`} replace />;
}

function ListEditRedirect({ listPath }: { listPath: string }): ReactElement {
  const { id } = useParams<{ id: string }>();
  if (!id || !isUuid(id)) {
    return <Navigate to={listPath} replace />;
  }
  return <Navigate to={`${listPath}?edit=${id}`} replace />;
}

export function MeasurementUnitEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/materials/measurement-units" />;
}

export function MaterialCategoryEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/materials/categories" />;
}

export function MaterialEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/materials" />;
}

export function EquipmentCategoryEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/equipments/categories" />;
}

export function EquipmentEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/equipments" />;
}

export function PartnerEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/partners" />;
}

export function FinancialCategoryEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/financial-categories" />;
}

export function PaymentMethodEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/payment-methods" />;
}

export function ServiceTaxCodeEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/service-tax-codes" />;
}

export function ServiceFrontModelEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/project-service-fronts-models" />;
}

export const LegacyServiceTypeEditRedirect = ServiceFrontModelEditRedirect;

export function ChecklistModelEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/checklist-models" />;
}

export function DocumentCategoryEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/document-categories" />;
}

export function OccurrenceEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/occurrences" />;
}

export function PhotoTagEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/photo-tags" />;
}

export function ProjectPriorityEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/project-priorities" />;
}

export function ProjectTypeEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/project-types" />;
}

export function UsersEditRedirect(): ReactElement {
  const { userId } = useParams<{ userId: string }>();
  if (!userId || !isUuid(userId)) {
    return <Navigate to="/settings/users" replace />;
  }
  return <Navigate to={`/settings/users?edit=${userId}`} replace />;
}

export function PermissionTemplateEditRedirect(): ReactElement {
  return <ListEditRedirect listPath="/settings/permissions" />;
}

function ListKeyEditRedirect({ listPath }: { listPath: string }): ReactElement {
  const { status } = useParams<{ status: string }>();
  const key = status?.trim();
  if (!key) {
    return <Navigate to={listPath} replace />;
  }
  return <Navigate to={`${listPath}?edit=${encodeURIComponent(key)}`} replace />;
}

export function ProjectStatusEditRedirect(): ReactElement {
  return <ListKeyEditRedirect listPath="/settings/project-statuses" />;
}

export function ProjectBudgetStatusEditRedirect(): ReactElement {
  return <ListKeyEditRedirect listPath="/settings/project-budget-statuses" />;
}

export function ProjectClientBudgetStatusEditRedirect(): ReactElement {
  return <ListKeyEditRedirect listPath="/settings/project-client-budget-statuses" />;
}

export function ServiceFrontDeadlineStatusEditRedirect(): ReactElement {
  return <ListKeyEditRedirect listPath="/settings/project-service-fronts-deadline-statuses" />;
}
